import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FaStar } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HomePhaseShell, HomePhaseBadge } from "@/components/v2/home/HomePhaseShell";

export interface PollOptionVM {
  id: string;
  label: string;
  voteCount: number;
  isSelected: boolean;
}

interface HomePollCardProps {
  options: PollOptionVM[];
  closingPoll: boolean;
  canClose: boolean;
  isAdmin: boolean;
  onVote: (id: string) => void;
  onClose: () => void;
  headerAction?: ReactNode;
}

/** Legacy (polls v1) poll_open UI — single vote per option, admin closes manually. */
export function HomePollCard({
  options,
  closingPoll,
  canClose,
  isAdmin,
  onVote,
  onClose,
  headerAction,
}: HomePollCardProps) {
  const { t } = useTranslation();
  const maxVotes = options.reduce((max, o) => Math.max(max, o.voteCount), 0);

  return (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowPollOpen")}
      title={t("v2.home.pollTitle")}
      eyebrowBadge={<HomePhaseBadge tone="primary">{t("v2.home.pollOpenBadge")}</HomePhaseBadge>}
      headerAction={headerAction}
      adminSection={
        isAdmin ? (
          <Button
            onClick={onClose}
            disabled={!canClose || closingPoll}
            className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
          >
            {closingPoll ? t("common.loading") : t("v2.home.closePoll")}
          </Button>
        ) : undefined
      }
      adminZone={
        isAdmin
          ? {
              title: t("v2.home.adminZoneClosePollTitle"),
              description: t("v2.home.adminZoneClosePollDesc"),
              actionRequired: true,
            }
          : undefined
      }
    >
      <div className="space-y-3">
        {options.map((opt) => {
          const isFrontrunner = maxVotes > 0 && opt.voteCount === maxVotes;
          const handleSelect = () => onVote(opt.id);

          return (
            <div
              key={opt.id}
              role="button"
              tabIndex={0}
              onClick={handleSelect}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect();
                }
              }}
              className={`relative flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer min-h-[44px] ${
                opt.isSelected
                  ? "border-soccer-primary bg-soccer-primary/10 shadow-lg scale-[1.01]"
                  : isFrontrunner
                    ? "border-amber-400/70 bg-amber-400/10 hover:border-amber-400"
                    : "border-border hover:border-soccer-primary/50 hover:bg-accent/50"
              }`}
            >
              <div className="space-y-1">
                <div className="font-semibold text-lg flex items-center gap-2">
                  {opt.label}
                  {isFrontrunner && (
                    <Badge className="bg-amber-400 text-black font-bold gap-1 px-2 py-0.5">
                      <FaStar className="text-[10px]" />
                      {t("v2.home.frontrunner")}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("v2.home.voteCount", { count: opt.voteCount })}
                </p>
              </div>
              <Button
                variant={opt.isSelected ? "default" : "outline"}
                size="sm"
                className="min-h-[44px] shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect();
                }}
              >
                {opt.isSelected ? t("v2.home.voted") : t("v2.home.voteButton")}
              </Button>
            </div>
          );
        })}
      </div>
    </HomePhaseShell>
  );
}
